import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { branchService } from '@/services/branchService'
import { reportService, SalesRevenueEntry } from '@/services/reportService'
import { BranchEntity } from '@/api/types'
import { Button } from '@/components/ui/button'
import { getLogoPath } from '@/components/shared/Logo'
import { formatCurrency } from '@/lib/format'

const formatDate = (value?: string) => {
  if (!value) return '-'
  try {
    const date = new Date(value)
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return value
  }
}

const formatAmount = (value?: number) => formatCurrency(Number(value ?? 0))

const buildAddress = (entity?: Partial<BranchEntity> | null) => {
  if (!entity) return ''
  const source = entity as Record<string, any>
  const parts = [
    source.address,
    source.addressEn,
    source.addressAr,
    source.street,
    source.citySubdivision,
    source.citySubdivisionAr,
    source.cityName,
    source.cityNameAr,
    source.city,
    source.countryName,
    source.countryNameAr,
    source.countryAr,
    source.zipCode,
    source.postalCode,
  ]
  return parts.filter((part) => typeof part === 'string' && part.trim().length > 0).join(', ')
}

export default function SalesRevenueReportPrint(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const fromDate = searchParams.get('fromDate') || ''
  const toDate = searchParams.get('toDate') || ''
  const branchIdParam = searchParams.get('branchId')
  const customerIdParam = searchParams.get('customerId')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<SalesRevenueEntry[]>([])
  const [totals, setTotals] = useState({
    totalAmount: 0,
  })
  const [branch, setBranch] = useState<BranchEntity | null>(null)

  useEffect(() => {
    if (!fromDate || !toDate || !branchIdParam) {
      setError('Missing required parameters')
      setLoading(false)
      return
    }

    const loadReport = async () => {
      try {
        setLoading(true)
        setError(null)
        const branchId = Number(branchIdParam)
        const customerId = customerIdParam ? Number(customerIdParam) : 0
        const fromDateTime = fromDate.includes('T') ? fromDate : `${fromDate}T00:00:00`
        const toDateTime = toDate.includes('T') ? toDate : `${toDate}T23:59:59`

        const [reportData, branchData] = await Promise.all([
          reportService.getSalesRevenueReport({
            fromDate: fromDateTime,
            toDate: toDateTime,
            branchId,
            customerId,
            pageNumber: 1,
            pageSize: 10000,
          }),
          branchService.getBranchById(branchId).catch(() => null),
        ])

        setRows(reportData?.data || [])
        setTotals({
          totalAmount: reportData?.totalAmount ?? 0,
        })
        setBranch(branchData)
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Unable to load report'
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    void loadReport()
  }, [fromDate, toDate, branchIdParam, customerIdParam])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Preparing report for print...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <p className="mb-4 text-lg font-semibold text-destructive">{error}</p>
        <Button onClick={() => navigate('/reports/sales-revenue')}>Back to Report</Button>
      </div>
    )
  }

  return (
    <div className="ledger-print-root p-6 bg-gray-50 print:bg-white">
      <style>{`
        @media print {
          @page { size: A4; margin: 8mm; }
          .no-print { display: none !important }
        }
        .ledger-print-root { font-family: 'Arial', 'Noto Naskh Arabic', sans-serif; color: #1f2937 }
        .ledger-wrapper { max-width: 100%; margin: 0 auto; background: #fff; padding: 10px; border: 0.5px solid #ddd }
        .ledger-main-title { text-align:center; font-size:16px; font-weight:800; margin-top:4px }
        .ledger-table { width:100%; border-collapse:collapse; font-size:9px; margin-top:4px }
        .ledger-table th, .ledger-table td { border:none; border-bottom:1px solid #E5E7EB; padding:3px; }
        .ledger-table thead th { background:#F5F5F5; font-weight:700; font-size:9px }
        .ledger-total-row { background:#F5F5F5; font-weight:800 }
      `}</style>

      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/reports/sales-revenue')} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> Print Report
        </Button>
      </div>

      <div className="ledger-wrapper">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <div style={{ width: '30%', fontSize: 11 }}>
            <div><strong>{branch?.nameEn || '-'}</strong></div>
            <div style={{ marginTop: 4 }}><strong>Address:</strong> {buildAddress(branch) || '-'}</div>
            <div style={{ marginTop: 2 }}><strong>Email:</strong> {branch?.email || '-'}</div>
            <div style={{ marginTop: 2 }}><strong>Phone:</strong> {branch?.phone || '-'}</div>
          </div>
          <div style={{ width: '40%', textAlign: 'center' }}>
            <img src={getLogoPath()} alt="Logo" style={{ height: 55, objectFit: 'contain', margin: '0 auto' }} />
          </div>
          <div style={{ width: '30%', textAlign: 'right', fontSize: 11 }}>
            <div><strong>{branch?.nameAr || branch?.nameEn || '-'}</strong></div>
            <div style={{ marginTop: 4 }}><strong>العنوان:</strong> {branch?.addressAr || buildAddress(branch) || '--'}</div>
            <div style={{ marginTop: 2 }}><strong>البريد الإلكتروني:</strong> {branch?.email || '-'}</div>
            <div style={{ marginTop: 2 }}><strong>هاتف:</strong> {branch?.phone || '-'}</div>
          </div>
        </div>

        <div style={{ backgroundColor: '#0A1E60', height: 3, margin: '8px 0' }} />
        <div className="ledger-main-title">Sales Revenue Report</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', background: '#E5E7EB', padding: 8, margin: '8px 0', borderRadius: 4 }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 10, marginBottom: 2 }}>FROM DATE</div>
            <div style={{ fontSize: 11 }}>{formatDate(fromDate)}</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 10, marginBottom: 2 }}>TO DATE</div>
            <div style={{ fontSize: 11 }}>{formatDate(toDate)}</div>
          </div>
        </div>

        <table className="ledger-table">
          <thead>
            <tr>
              <th style={{ width: '16%' }}>DATE</th>
              <th style={{ width: '28%' }}>CUSTOMER</th>
              <th style={{ width: '36%' }}>DESCRIPTION</th>
              <th style={{ width: '20%', textAlign: 'right' }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: 16 }}>No entries available</td>
              </tr>
            ) : (
              rows.map((entry, idx) => (
                <tr key={idx}>
                  <td>{formatDate(entry.createdDate)}</td>
                  <td>{entry.customer || '-'}</td>
                  <td>{entry.description || '-'}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(entry.amount)}</td>
                </tr>
              ))
            )}
            <tr className="ledger-total-row">
              <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>TOTAL</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(totals.totalAmount)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
